// ==================== INTERFACE ADMIN CONGÉS COMPLÈTE CORRIGÉE ====================
// Remplace frontend/src/components/BalanceManagement.js

import React, { useState, useEffect } from 'react';
import { useTranslation } from '../i18n/LanguageContext';
import { API_BASE, getCsrfToken } from '../config';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Home, DollarSign, TrendingUp, PieChart as PieChartIcon } from 'lucide-react';

const BalanceManagement = ({ user, onNavigate }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [balances, setBalances] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [stats, setStats] = useState({});
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingBalance, setEditingBalance] = useState(null);
  const [editingLeaveType, setEditingLeaveType] = useState(null);
  const [showBulkEdit, setShowBulkEdit] = useState(false);

  // États pour les modals
  const [newLeaveTypeData, setNewLeaveTypeData] = useState({
    name: '',
    description: '',
    max_days_per_year: '',
    is_paid: true,
    color: '#3498db',
    requires_approval: true,
    // 🚀 PHASE 1: Nouveaux champs secteur public
    code_administration: '',
    legislation_reference: '',
    eligibility_criteria: '',
    min_service_years: '',
    category: '',
    requires_medical_certificate: false,
    requires_hr_approval: false,
    sector_specific: ''
  });

  const [bulkEditData, setBulkEditData] = useState({
    leaveTypeId: '',
    newAllocation: ''
  });

  useEffect(() => {
    fetchData();
  }, [year, activeTab]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      if (activeTab === 'overview' || activeTab === 'employees' || activeTab === 'reports') {
        await fetchBalances();
      }
      if (activeTab === 'types' || activeTab === 'overview' || activeTab === 'reports') {
        await fetchLeaveTypes();
      }
    } catch (err) {
      setError(t('balance.error_loading_data'));
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBalances = async () => {
    const response = await fetch(`${API_BASE}/api/leaves/admin/balances?year=${year}`, {
      headers: getAuthHeaders()
    });
    const data = await response.json();
    
    if (data.success) {
      setBalances(data.balances || []);
      setStats(data.statistics || {});
    }
  };

  const fetchLeaveTypes = async () => {
    const response = await fetch(`${API_BASE}/api/leaves/types`, {
      headers: getAuthHeaders()
    });
    const data = await response.json();
    
    if (data.success) {
      setLeaveTypes(data.leaveTypes || []);
    }
  };

  const initializeBalances = async () => {
    try {
      setLoading(true);
      const csrf = await getCsrfToken();
      if (!csrf) {
        alert('❌ ' + (t('balance.error_initialization') || 'Please refresh the page and try again.'));
        setLoading(false);
        return;
      }
      const response = await fetch(`${API_BASE}/api/leaves/admin/balances/initializer`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'X-CSRF-Token': csrf },
        body: JSON.stringify({ year, resetExisting: true })
      });

      const data = await response.json();
      
      if (data.success) {
        alert(`✅ ${data.message}\n\n📊 ${t('balance.initialization_success').replace('{{balances}}', data.statistics.total_balances).replace('{{employees}}', data.statistics.employees_count)}`);
        await fetchBalances();
      } else {
        alert(`❌ ${t('balance.error')}: ${data.error}`);
      }
    } catch (err) {
      alert(`❌ ${t('balance.error_initialization')}`);
    } finally {
      setLoading(false);
    }
  };

  const updateBalance = async (balanceId, newValues) => {
    try {
      const csrf = await getCsrfToken();
      if (!csrf) {
        alert('❌ ' + (t('balance.error_update') || 'Please refresh the page and try again.'));
        return;
      }
      const response = await fetch(`${API_BASE}/api/leaves/admin/balances/${balanceId}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'X-CSRF-Token': csrf },
        body: JSON.stringify(newValues)
      });

      const data = await response.json();
      
      if (data.success) {
        await fetchBalances();
        setEditingBalance(null);
        alert(`✅ ${t('balance.success_updated')}`);
      } else {
        alert(`❌ ${t('balance.error')}: ${data.error}`);
      }
    } catch (err) {
      alert(`❌ ${t('balance.error_update')}`);
    }
  };

  // ✅ UNE SEULE VERSION de updateLeaveType
  const updateLeaveType = async (typeId, updates) => {
    try {
      setLoading(true);
      const csrf = await getCsrfToken();
      if (!csrf) {
        alert('❌ ' + (t('balance.error_update') || 'Please refresh the page and try again.'));
        setLoading(false);
        return;
      }
      const response = await fetch(`${API_BASE}/api/leaves/types/${typeId}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'X-CSRF-Token': csrf },
        body: JSON.stringify({
          name: updates.name,
          description: updates.description,
          max_days_per_year: updates.max_days_per_year ? parseInt(updates.max_days_per_year) : null,
          is_paid: updates.is_paid,
          color: updates.color,
          requires_approval: updates.requires_approval,
          // 🚀 PHASE 1: Nouveaux champs secteur public
          code_administration: updates.code_administration || null,
          legislation_reference: updates.legislation_reference || null,
          eligibility_criteria: updates.eligibility_criteria || null,
          min_service_years: updates.min_service_years ? parseInt(updates.min_service_years) : null,
          category: updates.category || null,
          requires_medical_certificate: updates.requires_medical_certificate || false,
          requires_hr_approval: updates.requires_hr_approval || false,
          sector_specific: updates.sector_specific || null
        })
      });

      const data = await response.json();
      
      if (data.success) {
        await fetchLeaveTypes();
        setEditingLeaveType(null);
        alert(`✅ ${t('balance.success_leave_type_updated')}`);
      } else {
        alert(`❌ ${t('balance.error')}: ${data.error}`);
      }
    } catch (err) {
      alert(`❌ ${t('balance.error_update')}`);
      console.error('Erreur modification type:', err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ UNE SEULE VERSION de createLeaveType
  const createLeaveType = async () => {
    try {
      setLoading(true);
      const csrf = await getCsrfToken();
      if (!csrf) {
        alert('❌ ' + (t('balance.error_create') || 'Please refresh the page and try again.'));
        setLoading(false);
        return;
      }
      const response = await fetch(`${API_BASE}/api/leaves/types`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'X-CSRF-Token': csrf },
        body: JSON.stringify({
          name: editingLeaveType.name,
          description: editingLeaveType.description,
          max_days_per_year: editingLeaveType.max_days_per_year ? parseInt(editingLeaveType.max_days_per_year) : null,
          is_paid: editingLeaveType.is_paid !== false,
          color: editingLeaveType.color || '#3498db',
          requires_approval: editingLeaveType.requires_approval !== false,
          // 🚀 PHASE 1: Nouveaux champs secteur public
          code_administration: editingLeaveType.code_administration || null,
          legislation_reference: editingLeaveType.legislation_reference || null,
          eligibility_criteria: editingLeaveType.eligibility_criteria || null,
          min_service_years: editingLeaveType.min_service_years ? parseInt(editingLeaveType.min_service_years) : null,
          category: editingLeaveType.category || null,
          requires_medical_certificate: editingLeaveType.requires_medical_certificate || false,
          requires_hr_approval: editingLeaveType.requires_hr_approval || false,
          sector_specific: editingLeaveType.sector_specific || null
        })
      });

      const data = await response.json();
      
      if (data.success) {
        await fetchLeaveTypes();
        setEditingLeaveType(null);
        alert(`✅ ${t('balance.success_leave_type_created')}`);
      } else {
        alert(`❌ ${t('balance.error')}: ${data.error}`);
      }
    } catch (err) {
      alert(`❌ ${t('balance.error_create')}`);
      console.error('Erreur création type:', err);
    } finally {
      setLoading(false);
    }
  };

  const bulkUpdateBalances = async () => {
    if (!bulkEditData.leaveTypeId || !bulkEditData.newAllocation) {
      alert(`❌ ${t('balance.error_select_type_allocation')}`);
      return;
    }

    try {
      setLoading(true);
      const csrf = await getCsrfToken();
      if (!csrf) {
        alert('❌ ' + (t('balance.error') || 'Please refresh the page and try again.'));
        setLoading(false);
        return;
      }
      const payload = {
        leaveTypeId: String(bulkEditData.leaveTypeId).trim(),
        totalAllocated: parseFloat(bulkEditData.newAllocation) || 0,
        year: Number(year) || new Date().getFullYear()
      };
      const response = await fetch(`${API_BASE}/api/leaves/admin/balances/bulk-update`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'X-CSRF-Token': csrf },
        body: JSON.stringify(payload)
      });

      let data;
      try {
        data = await response.json();
      } catch (_) {
        throw new Error(response.statusText || 'Reponse invalide');
      }
      if (!response.ok) {
        throw new Error(data?.error || data?.details || `Erreur ${response.status}`);
      }
      if (data.success) {
        await fetchBalances();
        setShowBulkEdit(false);
        setBulkEditData({ leaveTypeId: '', newAllocation: '' });
        alert(`✅ ${data.message}`);
      } else {
        alert(`❌ ${t('balance.error')}: ${data.error || data.details || ''}`);
      }
    } catch (err) {
      alert(`❌ ${t('balance.error_bulk_update')}: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  // Grouper les soldes par employé
  const groupedBalances = balances.reduce((acc, balance) => {
    const key = balance.employee_id;
    if (!acc[key]) {
      acc[key] = {
        employee_id: balance.employee_id,
        employee_name: balance.employee_name,
        employee_code: balance.employee_code,
        department: balance.department,
        position: balance.position,
        employee_email: balance.employee_email,
        balances: []
      };
    }
    acc[key].balances.push(balance);
    return acc;
  }, {});

  const employees = Object.values(groupedBalances);
  const years = Array.from({length: 5}, (_, i) => new Date().getFullYear() - 2 + i);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '3px solid rgba(255, 255, 255, 0.2)',
            borderTop: '3px solid #ffffff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '16px' }}>{t('balance.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Header transparent */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '24px 20px 20px',
        background: 'transparent',
        backdropFilter: 'none',
        borderBottom: 'none',
        zIndex: 999,
        maxWidth: '100%'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '6px',
          gap: '16px'
        }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('home')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
                flexShrink: 0,
                width: '44px',
                height: '44px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={t('balance.back_to_home')}
            >
              <Home size={20} />
            </button>
          )}
          <div style={{ 
            fontSize: 'clamp(28px, 5vw, 40px)', 
            fontWeight: 700, 
            maxWidth: '100%',
            letterSpacing: '-0.5px',
            lineHeight: 1.2,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <DollarSign size={32} />
            {t('balance.title')}
          </div>
        </div>
        <p style={{
          fontSize: 'clamp(14px, 2vw, 16px)',
          color: 'rgba(255, 255, 255, 0.7)',
          margin: '8px 0 0 0',
          letterSpacing: '0.2px',
          fontWeight: 500
        }}>
          {t('balance.subtitle')}
        </p>
      </div>

      {/* Contenu principal */}
      <div className="balance-content" style={{
        paddingTop: '140px',
        paddingBottom: '40px',
        paddingLeft: '20px',
        paddingRight: '20px',
        maxWidth: '1600px',
        margin: '0 auto'
      }}>
      {/* En-tête avec onglets */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '20px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
      }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: 700,
          color: '#ffffff',
          marginBottom: '24px'
        }}>🏛️ {t('balance.admin_title')}</h1>
        
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '24px',
          flexWrap: 'wrap',
          overflowX: 'auto',
          paddingBottom: '8px'
        }}>
          {[
            { id: 'overview', label: t('balance.tab_overview'), icon: TrendingUp },
            { id: 'types', label: t('balance.tab_types'), icon: DollarSign },
            { id: 'employees', label: t('balance.tab_employees'), icon: '👥' },
            { id: 'reports', label: t('balance.tab_reports'), icon: PieChartIcon }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 24px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                background: activeTab === tab.id
                  ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
                  : 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: activeTab === tab.id ? '0 4px 16px rgba(59, 130, 246, 0.3)' : 'none'
              }}
            >
              {typeof tab.icon === 'string' ? tab.icon : <tab.icon size={18} />}
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            style={{
              padding: '12px 16px',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
              fontSize: '14px',
              outline: 'none',
              cursor: 'pointer'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            }}
          >
            {years.map(y => (
              <option key={y} value={y} style={{ background: '#1a1f3a', color: '#ffffff' }}>{y}</option>
            ))}
          </select>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowBulkEdit(true)}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #f97316, #ea580c)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(249, 115, 22, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.3)';
              }}
            >
              ⚡ {t('balance.bulk_edit')}
            </button>
            <button
              onClick={initializeBalances}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
              }}
            >
              🚀 {t('balance.reset_year').replace('{{year}}', year)}
            </button>
          </div>
        </div>
      </div>

      {/* Contenu selon l'onglet actif */}
      {activeTab === 'overview' && (
        <>
          {/* Statistiques */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  padding: '12px',
                  background: 'rgba(59, 130, 246, 0.2)',
                  borderRadius: '12px',
                  fontSize: '2rem'
                }}>
                  👥
                </div>
                <div>
                  <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', margin: '0 0 4px 0' }}>
                    {t('balance.stat_employees')}
                  </p>
                  <p style={{ fontSize: '24px', fontWeight: 700, color: '#ffffff', margin: 0 }}>
                    {stats.total_all_employees ?? stats.total_employees ?? 0}
                  </p>
                  {stats.total_all_employees != null && stats.total_employees != null && stats.total_all_employees !== stats.total_employees && (
                    <p style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', margin: '2px 0 0 0' }}>
                      {stats.total_employees} {t('balance.active_of_total') || 'active'} ({stats.total_all_employees} {t('balance.total') || 'total'})
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  padding: '12px',
                  background: 'rgba(16, 185, 129, 0.2)',
                  borderRadius: '12px',
                  fontSize: '2rem'
                }}>
                  📊
                </div>
                <div>
                  <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', margin: '0 0 4px 0' }}>
                    {t('balance.stat_total_allocated')}
                  </p>
                  <p style={{ fontSize: '24px', fontWeight: 700, color: '#10b981', margin: 0 }}>
                    {stats.total_allocated || 0}
                  </p>
                </div>
              </div>
            </div>

            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  padding: '12px',
                  background: 'rgba(249, 115, 22, 0.2)',
                  borderRadius: '12px',
                  fontSize: '2rem'
                }}>
                  📉
                </div>
                <div>
                  <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', margin: '0 0 4px 0' }}>
                    {t('balance.stat_total_used')}
                  </p>
                  <p style={{ fontSize: '24px', fontWeight: 700, color: '#f97316', margin: 0 }}>
                    {stats.total_used || 0}
                  </p>
                </div>
              </div>
            </div>

            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  padding: '12px',
                  background: 'rgba(168, 85, 247, 0.2)',
                  borderRadius: '12px',
                  fontSize: '2rem'
                }}>
                  💎
                </div>
                <div>
                  <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', margin: '0 0 4px 0' }}>
                    {t('balance.stat_total_remaining')}
                  </p>
                  <p style={{ fontSize: '24px', fontWeight: 700, color: '#a855f7', margin: 0 }}>
                    {stats.total_remaining || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', margin: '-8px 0 16px 0' }}>
            {t('balance.stats_include_all_employees')}
          </p>

          {/* 📊 GRAPHIQUES DASHBOARD (PHASE 3) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Graphique évolution utilisés vs restants */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">📈 {t('balance.chart_evolution_title')}</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={leaveTypes.map(type => {
                  const typeBalances = balances.filter(b => b.leave_type_id === type.id);
                  const totalAllocated = typeBalances.reduce((sum, b) => sum + parseFloat(b.total_allocated || 0), 0);
                  const totalUsed = typeBalances.reduce((sum, b) => sum + parseFloat(b.used_days || 0), 0);
                  const totalRemaining = totalAllocated - totalUsed;
                  
                  return {
                    name: type.name.length > 10 ? type.name.substring(0, 10) + '...' : type.name,
                    [t('balance.chart_allocated')]: totalAllocated,
                    [t('balance.chart_used')]: totalUsed,
                    [t('balance.chart_remaining')]: totalRemaining
                  };
                })}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey={t('balance.chart_allocated')} fill="#22c55e" />
                  <Bar dataKey={t('balance.chart_used')} fill="#f59e0b" />
                  <Bar dataKey={t('balance.chart_remaining')} fill="#8b5cf6" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Graphique camembert + Summary by Leave Type table */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">🥧 {t('balance.chart_distribution_title')}</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={leaveTypes.map(type => {
                      const typeBalances = balances.filter(b => b.leave_type_id === type.id);
                      const totalAllocated = typeBalances.reduce((sum, b) => sum + parseFloat(b.total_allocated || 0), 0);
                      
                      return {
                        name: type.name,
                        value: totalAllocated,
                        color: type.color
                      };
                    }).filter(item => item.value > 0)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {leaveTypes.map((type, index) => (
                      <Cell key={`cell-${index}`} fill={type.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>

              {/* Summary by Leave Type - Table */}
              <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-4">📋 {t('balance.summary_title')}</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead className="bg-gray-50">
                    <tr style={{ borderBottom: '2px solid #e5e7eb', background: '#f8fafc' }}>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ padding: '12px 10px', color: '#374151', fontWeight: 600 }}>{t('balance.table_type')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ padding: '12px 10px', color: '#374151', fontWeight: 600 }}>{t('balance.summary_allocated')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ padding: '12px 10px', color: '#374151', fontWeight: 600 }}>{t('balance.summary_used')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ padding: '12px 10px', color: '#374151', fontWeight: 600 }}>{t('balance.summary_remaining')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ padding: '12px 10px', color: '#374151', fontWeight: 600 }}>{t('balance.summary_usage_rate')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {leaveTypes.map(type => {
                      const typeBalances = balances.filter(b => b.leave_type_id === type.id);
                      const totalAllocated = typeBalances.reduce((sum, b) => sum + parseFloat(b.total_allocated || 0), 0);
                      const totalUsed = typeBalances.reduce((sum, b) => sum + parseFloat(b.used_days || 0), 0);
                      const totalRemaining = totalAllocated - totalUsed;
                      const usageRate = totalAllocated > 0 ? ((totalUsed / totalAllocated) * 100).toFixed(1) : 0;
                      const usageNum = parseFloat(usageRate);
                      return (
                        <tr key={type.id} className="hover:bg-gray-50" style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td className="px-4 py-4 whitespace-nowrap" style={{ padding: '12px 10px', color: '#ffffff' }}>
                            <div className="flex items-center gap-2" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: type.color }} />
                              <span className="text-sm font-medium" style={{ color: '#ffffff', fontWeight: 600 }}>{type.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium text-green-600" style={{ padding: '12px 10px', fontWeight: 600, color: '#059669' }}>{totalAllocated.toFixed(1)}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium text-orange-600" style={{ padding: '12px 10px', fontWeight: 600, color: '#ea580c' }}>{totalUsed.toFixed(1)}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium text-purple-600" style={{ padding: '12px 10px', fontWeight: 600, color: '#7c3aed' }}>{totalRemaining.toFixed(1)}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-right" style={{ padding: '12px 10px' }}>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${usageNum > 80 ? 'bg-red-100 text-red-800' : usageNum > 60 ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
                              {usageRate}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'types' && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          padding: '24px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#ffffff', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
              ⚙️ {t('balance.types_title')}
            </h2>
            <button
              onClick={() => setEditingLeaveType({ isNew: true, ...newLeaveTypeData })}
              style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
            >
              ➕ {t('balance.new_type')}
            </button>
          </div>

          <div style={{ background: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left', background: '#f8fafc' }}>
                    <th style={{ padding: '12px 10px', color: '#374151', fontWeight: 600 }}>{t('balance.table_type')}</th>
                    <th style={{ padding: '12px 10px', color: '#374151', fontWeight: 600 }}>{t('balance.table_description')}</th>
                    <th style={{ padding: '12px 10px', color: '#374151', fontWeight: 600 }}>{t('balance.table_days_per_year')}</th>
                    <th style={{ padding: '12px 10px', color: '#374151', fontWeight: 600 }}>{t('balance.table_paid')}</th>
                    <th style={{ padding: '12px 10px', color: '#374151', fontWeight: 600 }}>{t('balance.table_approval')}</th>
                    <th style={{ padding: '12px 10px', color: '#374151', fontWeight: 600 }}>{t('balance.table_actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveTypes.map(type => (
                    <tr key={type.id} style={{ borderBottom: '1px solid #e5e7eb', background: '#ffffff' }}>
                      <td style={{ padding: '12px 10px', color: '#111827' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: type.color, flexShrink: 0 }} />
                          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>{type.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 10px', color: '#64748b', fontSize: '0.875rem' }}>{type.description || '—'}</td>
                      <td style={{ padding: '12px 10px', color: '#111827' }}>{type.max_days_per_year ?? t('balance.unlimited')}</td>
                      <td style={{ padding: '12px 10px' }}>
                        <span style={{ padding: '4px 10px', fontSize: '0.75rem', fontWeight: 600, borderRadius: '12px', backgroundColor: type.is_paid ? '#dcfce7' : '#fee2e2', color: type.is_paid ? '#166534' : '#991b1b' }}>
                          {type.is_paid ? t('balance.paid') : t('balance.unpaid')}
                        </span>
                      </td>
                      <td style={{ padding: '12px 10px' }}>
                        <span style={{ padding: '4px 10px', fontSize: '0.75rem', fontWeight: 600, borderRadius: '12px', backgroundColor: type.requires_approval ? '#fef3c7' : '#dcfce7', color: type.requires_approval ? '#92400e' : '#166534' }}>
                          {type.requires_approval ? t('balance.approval_required') : t('balance.approval_automatic')}
                        </span>
                      </td>
                      <td style={{ padding: '12px 10px' }}>
                        <button type="button" onClick={() => setEditingLeaveType(type)} style={{ padding: '6px 12px', color: '#2563eb', background: 'none', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                          ✏️ {t('balance.edit')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'employees' && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          padding: '24px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#ffffff', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            👥 {t('balance.employees_title')}
          </h2>

          {employees.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px', background: 'rgba(255,255,255,0.05)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ fontSize: '4rem', marginBottom: '16px' }}>📋</div>
              <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '24px' }}>{t('balance.no_balances_found').replace('{{year}}', year)}</p>
              <button type="button" onClick={initializeBalances} style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                {t('balance.initialize_balances')}
              </button>
            </div>
          ) : (
            <div style={{ background: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left', background: '#f8fafc' }}>
                      <th style={{ padding: '12px 10px', color: '#374151', fontWeight: 600 }}>{t('balance.table_employee')}</th>
                      <th style={{ padding: '12px 10px', color: '#374151', fontWeight: 600 }}>{t('balance.table_department')}</th>
                      <th style={{ padding: '12px 10px', color: '#374151', fontWeight: 600 }}>{t('balance.table_leave_types')}</th>
                      <th style={{ padding: '12px 10px', color: '#374151', fontWeight: 600 }}>{t('balance.table_actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((employee) => (
                      <tr key={employee.employee_id} style={{ borderBottom: '1px solid #e5e7eb', background: '#ffffff' }}>
                        <td style={{ padding: '12px 10px', color: '#111827', verticalAlign: 'top' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '0.875rem' }}>
                              {employee.employee_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '??'}
                            </div>
                            <div>
                              <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>{employee.employee_name}</div>
                              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{employee.employee_email}</div>
                              <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{employee.employee_code}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '12px 10px', color: '#111827', verticalAlign: 'top' }}>
                          <div style={{ fontSize: '0.875rem' }}>{employee.department || t('balance.not_defined')}</div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{employee.position || t('balance.not_defined')}</div>
                        </td>
                        <td style={{ padding: '12px 10px', verticalAlign: 'top' }}>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                              <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                                  <th style={{ padding: '8px 10px', textAlign: 'left', color: '#374151', fontWeight: 600 }}>{t('balance.table_type')}</th>
                                  <th style={{ padding: '8px 10px', textAlign: 'right', color: '#374151', fontWeight: 600 }}>{t('balance.summary_allocated')}</th>
                                  <th style={{ padding: '8px 10px', textAlign: 'right', color: '#374151', fontWeight: 600 }}>{t('balance.summary_used')}</th>
                                  <th style={{ padding: '8px 10px', textAlign: 'right', color: '#374151', fontWeight: 600 }}>{t('balance.summary_remaining')}</th>
                                  <th style={{ padding: '8px 10px', textAlign: 'right', color: '#374151', fontWeight: 600 }}>{t('balance.table_actions')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {employee.balances.map((balance) => (
                                  <tr key={balance.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                    <td style={{ padding: '8px 10px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: balance.leave_type_color || '#94a3b8' }} />
                                        <span style={{ fontSize: '0.8rem', color: '#111827' }}>{balance.leave_type_name}</span>
                                      </div>
                                    </td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>{balance.total_allocated}</td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: '#ea580c' }}>{balance.used_days}</td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: '#7c3aed' }}>{balance.remaining_days}</td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                                      <button type="button" onClick={() => setEditingBalance(balance)} style={{ padding: '4px 8px', color: '#2563eb', background: 'none', border: 'none', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>
                                        ✏️ {t('balance.edit')}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                        <td style={{ padding: '12px 10px', verticalAlign: 'top' }}>
                          <button type="button" style={{ padding: '6px 12px', color: '#2563eb', background: 'none', border: 'none', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}>
                            📊 {t('balance.history')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal d'édition de solde */}
      {editingBalance && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('balance.modal_edit_balance_title')}
            </h3>
            
            <div className="mb-4 p-3 bg-gray-50 rounded">
              <p className="text-sm text-gray-600">
                <strong>{t('balance.modal_employee')}:</strong> {editingBalance.employee_name}
              </p>
              <p className="text-sm text-gray-600">
                <strong>{t('balance.modal_type')}:</strong> {editingBalance.leave_type_name}
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('balance.label_allocated_days')}
                </label>
                <input
                  type="number"
                  defaultValue={editingBalance.total_allocated}
                  onChange={(e) => setEditingBalance({...editingBalance, newAllocated: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('balance.label_used_days')}
                </label>
                <input
                  type="number"
                  defaultValue={editingBalance.used_days}
                  onChange={(e) => setEditingBalance({...editingBalance, newUsed: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditingBalance(null)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                {t('balance.cancel')}
              </button>
              <button
                onClick={() => updateBalance(editingBalance.id, {
                  total_allocated: parseFloat(editingBalance.newAllocated ?? editingBalance.total_allocated) || 0,
                  used_days: parseFloat(editingBalance.newUsed ?? editingBalance.used_days) || 0
                })}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {t('balance.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal modification en masse */}
      {showBulkEdit && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              ⚡ {t('balance.modal_bulk_edit_title')}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('balance.label_leave_type')}
                </label>
                <select
                  value={bulkEditData.leaveTypeId}
                  onChange={(e) => setBulkEditData({...bulkEditData, leaveTypeId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('balance.select_type')}</option>
                  {leaveTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('balance.label_new_allocation')}
                </label>
                <input
                  type="number"
                  value={bulkEditData.newAllocation}
                  onChange={(e) => setBulkEditData({...bulkEditData, newAllocation: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  placeholder={t('balance.placeholder_allocation')}
                />
              </div>
              
              <div className="p-3 bg-yellow-50 rounded">
                <p className="text-sm text-yellow-800">
                  ⚠️ {t('balance.warning_bulk_edit')}
                </p>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowBulkEdit(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                {t('balance.cancel')}
              </button>
              <button
                onClick={bulkUpdateBalances}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
              >
                {t('balance.apply_to_all')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Edit / New Leave Type */}
      {editingLeaveType && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)', overflowY: 'auto', zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 16px 40px' }}>
          <div style={{ background: '#ffffff', borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', width: '100%', maxWidth: '560px', marginTop: '40px', marginBottom: '40px', overflow: 'hidden' }}>
            <div style={{ padding: '24px 24px 0', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', margin: 0, marginBottom: '24px' }}>
                {editingLeaveType.isNew ? t('balance.modal_create_type_title') : t('balance.modal_edit_type_title')}
              </h3>

              {/* Main fields */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    {t('balance.label_type_name')} <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={editingLeaveType.name || ''}
                    onChange={(e) => setEditingLeaveType({ ...editingLeaveType, name: e.target.value })}
                    placeholder={t('balance.placeholder_type_name')}
                    style={{ width: '100%', padding: '12px 14px', fontSize: '14px', border: '1px solid #e5e7eb', borderRadius: '10px', color: '#111827', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    {t('balance.label_description')}
                  </label>
                  <textarea
                    value={editingLeaveType.description || ''}
                    onChange={(e) => setEditingLeaveType({ ...editingLeaveType, description: e.target.value })}
                    placeholder={t('balance.placeholder_description')}
                    rows={3}
                    style={{ width: '100%', padding: '12px 14px', fontSize: '14px', border: '1px solid #e5e7eb', borderRadius: '10px', color: '#111827', outline: 'none', resize: 'vertical', minHeight: '80px', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    {t('balance.label_max_days')}
                  </label>
                  <input
                    type="number"
                    value={editingLeaveType.max_days_per_year ?? ''}
                    onChange={(e) => setEditingLeaveType({ ...editingLeaveType, max_days_per_year: e.target.value })}
                    placeholder={t('balance.placeholder_max_days')}
                    min={0}
                    style={{ width: '100%', padding: '12px 14px', fontSize: '14px', border: '1px solid #e5e7eb', borderRadius: '10px', color: '#111827', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                      {t('balance.label_color')}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <input
                        type="color"
                        value={editingLeaveType.color || '#3498db'}
                        onChange={(e) => setEditingLeaveType({ ...editingLeaveType, color: e.target.value })}
                        style={{ width: '48px', height: '40px', border: '1px solid #e5e7eb', borderRadius: '10px', cursor: 'pointer', padding: 0 }}
                      />
                      <span style={{ fontSize: '13px', color: '#6b7280' }}>{editingLeaveType.color || '#3498db'}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
                      <input
                        type="checkbox"
                        checked={editingLeaveType.is_paid !== false}
                        onChange={(e) => setEditingLeaveType({ ...editingLeaveType, is_paid: e.target.checked })}
                        style={{ width: '18px', height: '18px', accentColor: '#2563eb' }}
                      />
                      <span>{t('balance.label_paid_leave')}</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
                      <input
                        type="checkbox"
                        checked={editingLeaveType.requires_approval !== false}
                        onChange={(e) => setEditingLeaveType({ ...editingLeaveType, requires_approval: e.target.checked })}
                        style={{ width: '18px', height: '18px', accentColor: '#2563eb' }}
                      />
                      <span>{t('balance.label_approval_required')}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Optional: Public sector section */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', margin: '0 0 16px 0' }}>
                🏛️ {t('balance.section_public_sector_title')}
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#6b7280', marginBottom: '6px' }}>{t('balance.label_admin_code')}</label>
                    <input
                      type="text"
                      value={editingLeaveType.code_administration || ''}
                      onChange={(e) => setEditingLeaveType({ ...editingLeaveType, code_administration: e.target.value })}
                      placeholder={t('balance.placeholder_admin_code')}
                      style={{ width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #e5e7eb', borderRadius: '8px', color: '#111827', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#6b7280', marginBottom: '6px' }}>{t('balance.label_sector')}</label>
                    <select
                      value={editingLeaveType.sector_specific || ''}
                      onChange={(e) => setEditingLeaveType({ ...editingLeaveType, sector_specific: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #e5e7eb', borderRadius: '8px', color: '#111827', outline: 'none', background: '#fff', boxSizing: 'border-box' }}
                    >
                      <option value="">{t('balance.all_sectors')}</option>
                      <option value="public">{t('balance.sector_public')}</option>
                      <option value="privé">{t('balance.sector_private')}</option>
                      <option value="mixte">{t('balance.sector_mixed')}</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#6b7280', marginBottom: '6px' }}>{t('balance.label_legal_reference')}</label>
                  <input
                    type="text"
                    value={editingLeaveType.legislation_reference || ''}
                    onChange={(e) => setEditingLeaveType({ ...editingLeaveType, legislation_reference: e.target.value })}
                    placeholder={t('balance.placeholder_legal_reference')}
                    style={{ width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #e5e7eb', borderRadius: '8px', color: '#111827', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#6b7280', marginBottom: '6px' }}>{t('balance.label_min_service_years')}</label>
                    <input
                      type="number"
                      value={editingLeaveType.min_service_years ?? ''}
                      onChange={(e) => setEditingLeaveType({ ...editingLeaveType, min_service_years: e.target.value })}
                      placeholder={t('balance.placeholder_min_service_years')}
                      min={0}
                      style={{ width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #e5e7eb', borderRadius: '8px', color: '#111827', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#6b7280', marginBottom: '6px' }}>{t('balance.label_category')}</label>
                    <select
                      value={editingLeaveType.category || ''}
                      onChange={(e) => setEditingLeaveType({ ...editingLeaveType, category: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #e5e7eb', borderRadius: '8px', color: '#111827', outline: 'none', background: '#fff', boxSizing: 'border-box' }}
                    >
                      <option value="">{t('balance.category_not_specified')}</option>
                      <option value="ordinaire">{t('balance.category_ordinary')}</option>
                      <option value="exceptionnel">{t('balance.category_exceptional')}</option>
                      <option value="special">{t('balance.category_special')}</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', color: '#374151' }}>
                    <input type="checkbox" checked={editingLeaveType.requires_medical_certificate || false} onChange={(e) => setEditingLeaveType({ ...editingLeaveType, requires_medical_certificate: e.target.checked })} style={{ width: '16px', height: '16px', accentColor: '#2563eb' }} />
                    <span>{t('balance.label_medical_certificate')}</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', color: '#374151' }}>
                    <input type="checkbox" checked={editingLeaveType.requires_hr_approval || false} onChange={(e) => setEditingLeaveType({ ...editingLeaveType, requires_hr_approval: e.target.checked })} style={{ width: '16px', height: '16px', accentColor: '#2563eb' }} />
                    <span>{t('balance.label_hr_approval')}</span>
                  </label>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#6b7280', marginBottom: '6px' }}>{t('balance.label_eligibility_criteria')}</label>
                  <textarea
                    value={editingLeaveType.eligibility_criteria || ''}
                    onChange={(e) => setEditingLeaveType({ ...editingLeaveType, eligibility_criteria: e.target.value })}
                    placeholder={t('balance.placeholder_eligibility')}
                    rows={2}
                    style={{ width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #e5e7eb', borderRadius: '8px', color: '#111827', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            </div>

            <div style={{ padding: '20px 24px', display: 'flex', justifyContent: 'flex-end', gap: '12px', background: '#fff' }}>
              <button
                type="button"
                onClick={() => setEditingLeaveType(null)}
                style={{ padding: '10px 20px', fontSize: '14px', fontWeight: 600, color: '#374151', background: '#f3f4f6', border: 'none', borderRadius: '10px', cursor: 'pointer' }}
              >
                {t('balance.cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (editingLeaveType.isNew) createLeaveType();
                  else updateLeaveType(editingLeaveType.id, editingLeaveType);
                }}
                disabled={!editingLeaveType.name || !editingLeaveType.name.trim()}
                style={{ padding: '10px 24px', fontSize: '14px', fontWeight: 600, color: '#fff', background: (editingLeaveType.name && editingLeaveType.name.trim()) ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#9ca3af', border: 'none', borderRadius: '10px', cursor: (editingLeaveType.name && editingLeaveType.name.trim()) ? 'pointer' : 'not-allowed' }}
              >
                {editingLeaveType.isNew ? t('balance.create') : t('balance.modify')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🚀 PHASE 4: ONGLET RAPPORTS */}
      {activeTab === 'reports' && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          padding: '24px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#ffffff', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            📈 {t('balance.reports_title')}
          </h2>

          <div style={{ marginBottom: '24px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={async () => {
                try {
                  setLoading(true);
                  const token = localStorage.getItem('token');
                  const response = await fetch(`${API_BASE}/api/leaves/admin/reports/export?year=${year}&format=csv`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  if (response.ok) {
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `soldes_conges_${year}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    try { if (a?.parentNode) a.parentNode.removeChild(a); } catch (e) {}
                    alert(`✅ ${t('balance.success_csv_exported')}`);
                  }
                } catch (err) {
                  alert(`❌ ${t('balance.error_export')}`);
                } finally {
                  setLoading(false);
                }
              }}
              style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              📥 {t('balance.export_csv')}
            </button>
          </div>

          <div style={{ background: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left', background: '#f8fafc' }}>
                    <th style={{ padding: '12px 10px', color: '#374151', fontWeight: 600 }}>{t('balance.report_name') || 'Report'}</th>
                    <th style={{ padding: '12px 10px', color: '#374151', fontWeight: 600 }}>{t('balance.report_description') || 'Description'}</th>
                    <th style={{ padding: '12px 10px', color: '#374151', fontWeight: 600 }}>{t('balance.table_actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#ffffff' }}>
                    <td style={{ padding: '12px 10px', color: '#111827', fontWeight: 600 }}>📊 {t('balance.report_department_title')}</td>
                    <td style={{ padding: '12px 10px', color: '#64748b' }}>{t('balance.report_department_desc')}</td>
                    <td style={{ padding: '12px 10px' }}>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            setLoading(true);
                            const token = localStorage.getItem('token');
                            const response = await fetch(`${API_BASE}/api/leaves/admin/reports/usage-by-department?year=${year}`, { headers: { 'Authorization': `Bearer ${token}` } });
                            const data = await response.json();
                            if (data.success) {
                              const lines = data.report.map(r => t('balance.report_line', { department: r.department, requests: r.total_requests, approved: r.approved_days })).join('\n');
                              alert(`📊 ${t('balance.report_by_department_title', { year })}:\n\n${lines}`);
                            }
                          } catch (err) { alert(`❌ ${t('balance.error_report')}`); } finally { setLoading(false); }
                        }}
                        style={{ padding: '6px 12px', color: '#2563eb', background: 'none', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                      >
                        {t('balance.view_report')}
                      </button>
                    </td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#ffffff' }}>
                    <td style={{ padding: '12px 10px', color: '#111827', fontWeight: 600 }}>✅ {t('balance.report_compliance_title')}</td>
                    <td style={{ padding: '12px 10px', color: '#64748b' }}>{t('balance.report_compliance_desc')}</td>
                    <td style={{ padding: '12px 10px' }}>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            setLoading(true);
                            const token = localStorage.getItem('token');
                            const response = await fetch(`${API_BASE}/api/leaves/admin/reports/compliance?year=${year}`, { headers: { 'Authorization': `Bearer ${token}` } });
                            const data = await response.json();
                            if (data.success) {
                              const alerts = data.report.alerts;
                              alert(`🚨 ${t('balance.compliance_alerts_title', { year })}:\n\n• ${t('balance.compliance_high_residuals', { count: alerts.high_residuals_count })}\n• ${t('balance.compliance_long_pending', { count: alerts.long_pending_count })}\n\n${t('balance.see_console_details')}`);
                              console.log('📊 Rapport conformité:', data.report);
                            }
                          } catch (err) { alert(`❌ ${t('balance.error_report')}`); } finally { setLoading(false); }
                        }}
                        style={{ padding: '6px 12px', color: '#2563eb', background: 'none', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                      >
                        {t('balance.view_report')}
                      </button>
                    </td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#ffffff' }}>
                    <td style={{ padding: '12px 10px', color: '#111827', fontWeight: 600 }}>🔮 {t('balance.report_forecast_title')}</td>
                    <td style={{ padding: '12px 10px', color: '#64748b' }}>{t('balance.report_forecast_desc')}</td>
                    <td style={{ padding: '12px 10px' }}>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            setLoading(true);
                            const token = localStorage.getItem('token');
                            const response = await fetch(`${API_BASE}/api/leaves/admin/reports/forecast?year=${year}`, { headers: { 'Authorization': `Bearer ${token}` } });
                            const data = await response.json();
                            if (data.success) {
                              const atRisk = data.forecast.filter(f => f.at_risk);
                              alert(`🔮 ${t('balance.forecast_alert_title', { year })}:\n\n• ${t('balance.forecast_months_remaining', { count: data.months_remaining })}\n• ${t('balance.forecast_types_at_risk', { count: atRisk.length })}\n\n${t('balance.see_console_details')}`);
                              console.log('📊 Prévisionnel:', data.forecast);
                            }
                          } catch (err) { alert(`❌ ${t('balance.error_forecast')}`); } finally { setLoading(false); }
                        }}
                        style={{ padding: '6px 12px', color: '#2563eb', background: 'none', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                      >
                        {t('balance.view_forecast')}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50">
          {error}
        </div>
      )}
      </div>

      {/* CSS pour animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (min-width: 768px) {
          .balance-content {
            padding: 160px 40px 40px !important;
          }
        }
        @media (min-width: 1024px) {
          .balance-content {
            padding: 180px 60px 60px !important;
          }
        }
        .balance-content {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default BalanceManagement;